import React from 'react';
import Lottie from 'lottie-react';

import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Checkbox from '@material-ui/core/Checkbox';
import TableBody from '@material-ui/core/TableBody';
import Chip from '@material-ui/core/Chip';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import BlockRoundedIcon from '@material-ui/icons/BlockRounded';
import CallMadeRoundedIcon from '@material-ui/icons/CallMadeRounded';
import { formattedAmount } from '@utils/format';
import ErrorOutlineRoundedIcon from '@material-ui/icons/ErrorOutlineRounded';

import useStyles from '@components/AccountsPayableCustomTable/styles';
import { AccountsPayableLinkErrorMsg } from '@constants/accountsPayableLink';

import processing from '@assets/lotties/processing.json';
import { HeadCell } from './EnhancedTableHead';

interface EnhancedTableBody {
  grouped: boolean;
  isCanceled: boolean;
  canSelect: boolean;
  isInProcess: boolean;
  rows: any[];
  isSelected: any;
  handleClick: (event: React.MouseEvent<unknown>, name: string) => void;
  headCells: HeadCell[];
  statusErrorLabel: string;
  statusSuccessLabel: string;
  totalVisibleColumns: number;
}

const EnhancedTableBody = (props: EnhancedTableBody) => {
  const {
    grouped,
    rows,
    isSelected,
    isCanceled,
    canSelect,
    handleClick,
    headCells,
    statusErrorLabel,
    statusSuccessLabel,
    isInProcess,
    totalVisibleColumns,
  } = props;

  const classes = useStyles({ grouped });

  return (
    <TableBody>
      {rows.map((row: any, index: number) => {
        const isItemSelected = isSelected(row.accountPayableId);
        const labelId = `enhanced-table-checkbox-${index}`;
        const cellClass =
          !isItemSelected || isCanceled ? classes.tableBodyCellUnselected : classes.tableBodyCell;
        const isError = !!row?.error;
        const errorMsg = row?.error as keyof typeof AccountsPayableLinkErrorMsg;

        return (
          <>
            <TableRow
              hover
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.accountPayableId}
            >
              <TableCell padding="checkbox" style={{ border: 0 }}>
                {canSelect && (
                  <Checkbox
                    onClick={(event) => handleClick(event, row.accountPayableId)}
                    disabled={isError}
                    color="primary"
                    checked={!isError ? isItemSelected : false}
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                )}
              </TableCell>
              {headCells.map((cell, index) => {
                if (cell.visible) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  let content: any = row[cell.id];
                  if (cell.id === 'provider') {
                    content = (
                      <>
                        <div>{row.businessName}</div>
                        <div>{row.clabe}</div>
                      </>
                    );
                  }
                  if (cell.id === 'status') {
                    content = (
                      <>
                        <div>
                          {row?.error ? (
                            <Chip
                              className={classes.chipError}
                              icon={<CancelRoundedIcon />}
                              label={statusErrorLabel}
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              className={classes.chipSuccess}
                              icon={<CheckCircleRoundedIcon />}
                              label={statusSuccessLabel}
                              variant="outlined"
                            />
                          )}
                        </div>
                      </>
                    );
                    if (isInProcess) {
                      content = (
                        <div>
                          <Chip
                            className={classes.chipOnProcess}
                            icon={<Lottie style={{ width: 15 }} animationData={processing} />}
                            label="En proceso"
                            variant="outlined"
                          />
                        </div>
                      );
                    }
                    if (isCanceled) {
                      content = (
                        <div>
                          <Chip
                            className={classes.chipCanceled}
                            icon={<BlockRoundedIcon />}
                            label="Cancelada"
                            variant="outlined"
                          />
                        </div>
                      );
                    }
                  }
                  if (cell.id === 'cepUrl') {
                    content = (
                      <div>
                        {row.cepUrl !== '' ? (
                          <Chip
                            onClick={() => window.open(row.cepUrl, '_blank')}
                            className={classes.chipCep}
                            icon={<CallMadeRoundedIcon />}
                            label="Comprobante"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    );
                  }
                  if (cell.id === 'amount') {
                    content = (
                      <>
                        {formattedAmount(row.amount, row.currency)}{' '}
                        <span className={classes.totalAmountCurrency}>{row.currency}</span>
                      </>
                    );
                  }

                  return (
                    <TableCell
                      key={index}
                      className={cellClass}
                      align={cell.align}
                      padding="normal"
                    >
                      <div>{content}</div>
                    </TableCell>
                  );
                }
                return null;
              })}
            </TableRow>
            {row?.error && !isCanceled && (
              <TableRow tabIndex={-1}>
                <TableCell
                  key={index + 'error'}
                  className={cellClass}
                  align="left"
                  padding="none"
                  colSpan={totalVisibleColumns}
                >
                  <div className={classes.rowError}>
                    <Chip
                      className={classes.chipError}
                      icon={<ErrorOutlineRoundedIcon />}
                      label={AccountsPayableLinkErrorMsg[errorMsg]}
                      variant="outlined"
                    />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </>
        );
      })}
    </TableBody>
  );
};

export default EnhancedTableBody;
